import React from "react";
import Parse from "parse";
import { observer } from "mobx-react-lite";
import { Card } from "antd";
import { Typography, Box, Link } from "@material-ui/core";

type Props = {};

export const PrivacyPolicyDataDonationRoute = observer((props: Props) => {
  return (
    <Card>
      <Typography variant="h4" gutterBottom>
        Informationen zum Datenschutz der Stimmspenden im Forschungsprojekt
        AntiScam
      </Typography>

      <Typography variant="body1" paragraph>
        Wir bedanken uns für Ihre Bereitschaft, das Forschungsprojekt „AntiScam"
        (folgend: Projekt) bei der empirischen Arbeit zu unterstützen. Der
        nachfolgende Text wurde für eine einfachere Lesbarkeit im generischen
        Maskulinum verfasst. Selbstverständlich werden alle Menschen jeglicher
        geschlechterspezifischer Identifizierung angesprochen.
      </Typography>

      <Typography variant="h5" gutterBottom>
        I. Projektinhalt und -ziel
      </Typography>

      <Typography variant="body1" paragraph>
        Das Projekt zielt darauf ab, Verbraucher vor den wachsenden Gefahren
        durch konversationellen Betrug zu schützen. Es verfolgt einen
        ganzheitlichen, sozio-technischen Ansatz mit vier Kernzielen:
      </Typography>

      <Box component="ol" sx={{ pl: 4 }}>
        <Box component="li" mb={1}>
          <Box component="span" fontWeight="bold">
            Bedrohungsanalyse:
          </Box>{" "}
          Entwicklung einer Taxonomie und Bedrohungskarte für KI-basierte
          Gesprächsbetrugsformen inklusive möglicher Abwehrmaßnahmen.
        </Box>
        <Box component="li" mb={1}>
          <Box component="span" fontWeight="bold">
            Erklärbare Betrugserkennung:
          </Box>{" "}
          Einsatz erklärbarer KI (XAI) zur Erkennung und Nachvollziehbarkeit von
          Angriffen mit synthetischen Stimmen.
        </Box>
        <Box component="li" mb={1}>
          <Box component="span" fontWeight="bold">
            Verbraucherbildung:
          </Box>{" "}
          Gestaltung eines Lernspiels zur Sensibilisierung und Stärkung der
          Selbstschutzkompetenz gegenüber Betrug.
        </Box>
        <Box component="li" mb={1}>
          <Box component="span" fontWeight="bold">
            Rechtliche Analyse:
          </Box>{" "}
          Untersuchung rechtlicher Lücken im Umgang mit KI-gestütztem
          Online-Betrug und Ableitung von Handlungsempfehlungen.
        </Box>
      </Box>

      <Typography variant="body1" paragraph>
        Insgesamt ergänzen sich die Teilziele gegenseitig. Die Bedrohungskarte
        liefert die Grundlage für die Entwicklung sowohl technischer
        Abwehrmaßnahmen als auch von Strategien zur Selbstverteidigung. Die
        technischen Schutzmechanismen bauen auf diesen Erkenntnissen auf, um
        wirksame und nachvollziehbare Lösungen für Verbraucher bereitzustellen.
        Die spielbasierte Verbraucherbildung vermittelt Selbstschutzstrategien,
        die sowohl auf der Bedrohungskarte als auch auf technischen
        Erkenntnissen beruhen, um Nutzer zu befähigen, sich aktiv gegen
        KI-basierte Angriffe zu schützen. In allen Bereichen werden zudem die
        rechtlichen Fragestellungen beleuchtet, die sich aus den neuen
        Missbrauchspotenzialen von KI-Systemen ergeben.
      </Typography>

      <Typography variant="h5" gutterBottom>
        I. Informationen über die Verarbeitung Ihrer Daten gemäß Art. 13 der
        Datenschutz-Grundverordnung (DSGVO)
      </Typography>

      <Typography variant="h6" gutterBottom>
        1. Rechtsgrundlage
      </Typography>

      <Typography variant="body1" paragraph>
        Rechtsgrundlage für die Erhebung und Verarbeitung der personenbezogenen
        Daten ist insbesondere Art. 6 Abs. 1 Satz 1 lit. a) DS-GVO sowie Art. 9
        Abs. 2 lit. a) DS-GVO (Einwilligung) für besondere Kategorien
        personenbezogener Daten, hier Stimmdaten. Die erhobenen Daten werden für
        die unter I. genannten wissenschaftliche Zwecke verarbeitet.
      </Typography>

      <Typography variant="body1" paragraph>
        Aufgrund des wissenschaftlichen Charakters des Projekts sind für die
        Verarbeitung neben der Einwilligung noch Art. 6 Abs. 1 Satz 1 lit. e)
        DSGVO, § 3 BDSG iVm. §§ 3 Abs. 1 Satz 1, 70 Abs. 1 HG NRW für
        personenbezogene Daten und Art. 9 Abs. 2 lit. j), 89 Abs. 1, 2 DSGVO, §§
        3, 27 Abs. 1 BDSG, § 17 Abs. 1 DSG NRW iVm. §§ 3 Abs. 1 Satz 1, 70 Abs.
        1 HG NRW (Datenverarbeitung personenbezogener Daten zu
        wissenschaftlichen Forschungszwecken) für personenbezogene Daten der
        besonderen Kategorien als Rechtsgrundlage einschlägig.
      </Typography>

      <Typography variant="h6" gutterBottom>
        2. Erhobene Daten und Verarbeitungszwecke
      </Typography>

      <Typography variant="body1" paragraph>
        (1) Die Stimmdaten werden für Forschungszwecke gespeichert und
        ausschließlich zum Zweck des KI-Spiels zur Sensibilisierung und Bildung
        der Gesellschaft durch das Projekt verwendet. Dies umfasst das
        Modell-Training als auch die Einspeisung der Stimmdaten in das KI-Spiel.
      </Typography>

      <Typography variant="body1" paragraph>
        (2) Die Stimmdaten werden in Bezug auf das KI-Spiel fünf Jahre über die
        Projektlaufzeit hinaus gespeichert, sofern keine ausdrückliche Löschung
        gewünscht ist.
      </Typography>

      <Typography variant="body1" paragraph>
        (3) Die Stimmdaten werden in Bezug auf das Modell-Training fünf Jahre
        über die Projektlaufzeit hinaus gespeichert, sofern keine ausdrückliche
        Löschung gewünscht ist.
      </Typography>

      <Typography variant="body1" paragraph>
        (4) Bei Stimmdatenabgabe wird der Zeitpunkt der Aufnahme erfasst.
      </Typography>

      <Typography variant="body1" paragraph>
        (5) Bei jedem Zugriff auf Inhalte der Website werden vorübergehend Daten
        gespeichert, die möglicherweise eine Identifizierung zulassen. Die
        folgenden Daten werden hierbei erhoben:
      </Typography>

      <Box component="ul" sx={{ pl: 4 }}>
        <Box component="li">Datum und Uhrzeit des Zugriffs</Box>
        <Box component="li">Meldung, wenn der Abruf nicht erfolgreich war</Box>
        <Box component="li">Benutzerauthentifizierung (Logindaten)</Box>
      </Box>

      <Typography variant="body1" paragraph>
        Die vorübergehende Speicherung der Daten ist für den Ablauf eines
        Websitebesuchs erforderlich, um eine Auslieferung der Website zu
        ermöglichen. Eine weitere Speicherung in Protokolldateien erfolgt, um
        die Funktionsfähigkeit der Website und die Sicherheit der
        informationstechnischen Systeme sicherzustellen. In diesen Zwecken liegt
        auch unser berechtigtes Interesse an der Datenverarbeitung.
      </Typography>

      <Typography variant="body1" paragraph>
        (6) Die Angabe von weiteren Daten (Alter, Geschlecht) ist freiwillig.
      </Typography>

      <Typography variant="body1" paragraph>
        (7) Es erfolgt keine Weitergabe der Daten an Dritte.
      </Typography>

      <Typography variant="h5" gutterBottom>
        II. Betroffenenrechte
      </Typography>

      <Typography variant="body1" paragraph>
        Ihnen stehen Betroffenenrechte zu. Zur Inanspruchnahme der Rechte genügt
        eine Mitteilung in Textform (Brief oder E-Mail) an die Verantwortlichen.
        Die Datensicherheit hat auch in Bezug auf die Betroffenenrechte einen
        hohen Stellenwert, weshalb die Geltendmachung von Betroffenenrechten nur
        nach einer zweifelsfreien Identifikation der betroffenen Person möglich
        ist.
      </Typography>

      <Typography variant="body1" paragraph>
        (1){" "}
        <Box component="span" fontWeight="bold">
          Widerruf (Art. 7 Abs. 3 DSGVO):
        </Box>{" "}
        Die Angabe personenbezogener Daten ist freiwillig. Die Einwilligung zur
        Verarbeitung der personenbezogenen Daten kann jederzeit nach Art. 7 Abs.
        3 DSGVO mit der Folge widerrufen werden, dass die personenbezogenen
        Daten der betreffenden Person nicht weiterverarbeitet werden.
      </Typography>

      <Typography variant="body1" paragraph>
        (2){" "}
        <Box component="span" fontWeight="bold">
          Auskunftsrecht (Art. 15 DSGVO):
        </Box>{" "}
        Die Betroffenen haben das Recht, jederzeit Auskunft über die zu ihrer
        Person verarbeiteten Daten sowie die möglichen Empfänger dieser Daten
        verlangen zu können. Ihnen steht eine Antwort innerhalb einer Frist von
        einem Monat nach Eingang des Auskunftsersuchens zu.
      </Typography>

      <Typography variant="body1" paragraph>
        (3){" "}
        <Box component="span" fontWeight="bold">
          Recht auf Berichtigung, Löschung und Einschränkung (Art. 16–18 DSGVO):
        </Box>{" "}
        Die Betroffenen können jederzeit gegenüber den Projektverantwortlichen
        die Berichtigung, Löschung ihrer personenbezogenen Daten bzw. die
        Einschränkung der Verarbeitung verlangen.
      </Typography>

      <Typography variant="body1" paragraph>
        (4){" "}
        <Box component="span" fontWeight="bold">
          Recht auf Datenübertragbarkeit (Art. 20 DSGVO):
        </Box>{" "}
        Die Betroffenen können verlangen, dass der Verantwortliche ihnen ihre
        personenbezogenen Daten in maschinenlesbarem Format übermittelt.
        Alternativ können sie die direkte Übermittlung der von ihnen
        bereitgestellten personenbezogenen Daten an einen anderen
        Verantwortlichen verlangen, soweit dies möglich ist. Die Übertragbarkeit
        gilt nur für personenbezogene Daten, die mithilfe automatisierter
        Verfahren verarbeitet werden.
      </Typography>

      <Typography variant="body1" paragraph>
        (5){" "}
        <Box component="span" fontWeight="bold">
          Widerspruchsrecht (Art. 21 DSGVO):
        </Box>{" "}
        Liegen bei der betroffenen Person Gründe vor, die sich aus ihrer
        besonderen Situation ergeben, kann sie jederzeit der Verarbeitung ihrer
        personenbezogenen Daten widersprechen. Diese werden sodann nicht mehr
        weiterverarbeitet, es sei denn, die Verantwortlichen im Projekt können
        zwingende schutzwürdige Gründe für die Verarbeitung nachweisen, die die
        Interessen, Rechte und Freiheiten der betroffenen Person überwiegen,
        oder die Verarbeitung dient der Geltendmachung, Ausübung oder
        Verteidigung von Rechtsansprüchen.
      </Typography>

      <Typography variant="body1" paragraph>
        (6){" "}
        <Box component="span" fontWeight="bold">
          Beschwerderecht (Art. 77 DSGVO):
        </Box>{" "}
        Betroffene Personen können sich jederzeit an den Datenschutzbeauftragten
        der Universität Siegen (datenschutz@uni-siegen.de), an Frau Charlene
        Pfeiffer sowie bei einer Beschwerde nach Art. 77 DSGVO an die zuständige
        Aufsichtsbehörde zum Datenschutz wenden. Die zuständige Aufsichtsbehörde
        ist: Landesbeauftragte oder Landesbeauftragter für den Datenschutz und
        die Informationsfreiheit Nordrhein-Westfalen, LDI NRW (Kavalleriestraße
        2-4, 40213 Düsseldorf).
      </Typography>

      <Typography variant="body1" paragraph>
        Beachte Sie zudem, dass aufgrund des Forschungscharakters des Projekts
        die in Art. 15, 16, 18 und 21 DSGVO vorgesehenen Rechte insoweit
        beschränkt sind, als diese Rechte voraussichtlich die Verwirklichung des
        Forschungszwecks unmöglich macht oder ernsthaft beeinträchtigt und die
        Beschränkung für die Erfüllung der Forschungs- oder Statistikzwecke
        notwendig ist. Das Recht auf Auskunft gemäß Art. 15 DSGVO besteht
        darüber hinaus nicht, wenn die Daten für Zwecke der wissenschaftlichen
        Forschung erforderlich sind und die Auskunftserteilung einen
        unverhältnismäßigen Aufwand erfordern würde (§ 28 Abs. 2 BDSG).
      </Typography>

      <Typography variant="h5" gutterBottom>
        III. Kontaktdaten
      </Typography>

      <Typography variant="body1" paragraph>
        Bei Fragen zum Projekt wenden Sie sich bitte an Prof. Dr. Gunnar Stevens
        (gunnar.stevens@uni-siegen.de). Bei Fragen zum Datenschutz wenden Sie
        sich bitte an Frau Charlene Pfeiffer (pfeiffer@recht.uni-siegen.de).
      </Typography>
    </Card>
  );
});
